import { Injectable } from "@angular/core";
import { Observable, finalize, from, tap } from "rxjs";
import { ModulesWithPages } from "../core/services/cases.service";
import { CaseModulePatchQPS, CaseModulePostQPS, CaseModulePostResponse, modulesGetQPS } from '../shared/models/modulesQPS';
import { ModulesState } from "../core/states/modules.state";
import { ModulesService } from "../core/services/modules.service";
import { CaseModule, Module } from "../shared/models/module";
import { Attention } from "../shared/models/attention";
import { ProvisionsFacade } from "./provisions.facade";
import { AttentionsState } from "../core/states/attentions.state";

export interface DeleteCaseModuleResult {
    refreshError?: unknown;
}

@Injectable({
    providedIn: 'root',
})
export class ModulesFacade {

    constructor(
        private modulesState: ModulesState,
        private modulesService: ModulesService,
        private provisionsFacade: ProvisionsFacade,
        private attentionsState: AttentionsState
    ) { }

    loadModulesWithFilters$(modulesQPS?: modulesGetQPS): Observable<ModulesWithPages> {
        this.modulesState.setLoadingGetModules(true);
        const promise: Promise<ModulesWithPages> = new Promise((res, rej) => {

            this.modulesService.getModules(modulesQPS).pipe(
                //takeUntil(this.httpCancelService.onCancelPendingRequests()),
                tap(body => this.modulesState.setModules(body.data)),
                //finalize(() => this.casesState.setLoadingProvisionalControlIndicators(false))
                finalize(() => this.modulesState.setLoadingGetModules(false)),
                // finalize(() => this.casesState.setLoadingGettingPagination(false)),
            ).subscribe({
                next: response => {
                    // this.casesState.setMetaDataCases(modulesQPS);
                    // // this.casesState.setProvisionalControlIndicatorValues(response.data);
                    // this.casesState.setCases(response.data.cases);
                    res(response);
                },
                error: e => { rej(e) }
            })
        });
        return from(promise);
    }

    loadModuleByCode$(modulesQPS?: modulesGetQPS): Observable<ModulesWithPages> {
        this.modulesState.setLoadingGetModules(true);
        const promise: Promise<ModulesWithPages> = new Promise((res, rej) => {

            this.modulesService.getModules(modulesQPS).pipe(
                //takeUntil(this.httpCancelService.onCancelPendingRequests()),
                //finalize(() => this.casesState.setLoadingProvisionalControlIndicators(false))
                finalize(() => this.modulesState.setLoadingGetModules(false)),
                // finalize(() => this.casesState.setLoadingGettingPagination(false)),
            ).subscribe({
                next: response => {
                    // this.casesState.setMetaDataCases(modulesQPS);
                    // // this.casesState.setProvisionalControlIndicatorValues(response.data);
                    // this.casesState.setCases(response.data.cases);
                    res(response);
                },
                error: e => { rej(e) }
            })
        });
        return from(promise);
    }

    postModule(module: Module): Observable<Module> {
        this.modulesState.setLoadingCreatingModule(true);
        const promise: Promise<any> = new Promise((res, rej) => {
            this.modulesService.postModule(module).pipe(
                tap((newModule: Module) => {
                    this.modulesState.addModule(newModule);
                }),
                finalize(() => this.modulesState.setLoadingCreatingModule(false))
            ).subscribe({
                next: (response: Module) => {
                    res(response);
                },
                error: e => { rej(e) }
            })
        });
        return from(promise);
    }

    getModules$(): Observable<Module[]> {
        return this.modulesState.getModules$();
    };

    getModules(): Module[] {
        return this.modulesState.getModules();
    };

    patchModule(module: Module, id: number): Observable<Module> {
        this.modulesState.setLoadingUpdatingModule(true);
        const promise: Promise<any> = new Promise((res, rej) => {
            this.modulesService.patchModule(module, id).pipe(
                tap((newModule: Module) => {
                    this.modulesState.addModule(newModule);
                }),
                finalize(() => this.modulesState.setLoadingUpdatingModule(false))
            ).subscribe({
                next: (response: Module) => {
                    res(response);
                },
                error: e => { rej(e) }
            })
        });
        return from(promise);
    }

    isLoadingGettingModules$(): Observable<boolean> {
        return this.modulesState.isLoadingGetModules$();
    }

    isLoadingPatchingModule$(): Observable<boolean> {
        return this.modulesState.isLoadingUpdatingModule$();
    }

    isLoadingCreatingModule$(): Observable<boolean> {
        return this.modulesState.isLoadingCreatingModule$();
    }

    isLoadingGettingCaseModules$(): Observable<boolean> {
        return this.modulesState.isLoadingGettingCaseModules$();
    }

    getModulesCount$(): Observable<number> {
        return this.modulesState.getModulesCount$();
    }

    loadModulesCount() {
        const promise: Promise<any> = new Promise((res, rej) => {
            this.modulesService.getModulesCount().pipe(
                tap((data: any) => {
                    const count = typeof data === 'number'
                        ? data
                        : (typeof data?.count === 'number'
                            ? data.count
                            : (typeof data?.data === 'number' ? data.data : 0));
                    this.modulesState.setModulesCount(count);
                }),
            ).subscribe({
                next: (data: any) => {
                    const count = typeof data === 'number'
                        ? data
                        : (typeof data?.count === 'number'
                            ? data.count
                            : (typeof data?.data === 'number' ? data.data : 0));
                    res(count);
                },
                error: (e) => { rej(e); },
            });
        });
        return from(promise);
    }

    getCaseModules$(): Observable<CaseModule[]> {
        return this.modulesState.getCaseModules$();
    }

    loadCaseModules$(caseId: number): Observable<CaseModule[]> {
        this.modulesState.setLoadingGettingCaseModules(true);
        const promise: Promise<CaseModule[]> = new Promise((res, rej) => {

            this.modulesService.getCaseModules(caseId).pipe(
                //takeUntil(this.httpCancelService.onCancelPendingRequests()),
                tap(body => this.modulesState.setCaseModules(body)),
                //finalize(() => this.casesState.setLoadingProvisionalControlIndicators(false))
                finalize(() => this.modulesState.setLoadingGettingCaseModules(false)),
                // finalize(() => this.casesState.setLoadingGettingPagination(false)),
            ).subscribe({
                next: response => {
                    // this.casesState.setMetaDataCases(modulesQPS);
                    // // this.casesState.setProvisionalControlIndicatorValues(response.data);
                    // this.casesState.setCases(response.data.cases);
                    res(response);
                },
                error: e => { rej(e) }
            })
        });
        return from(promise);
    }

    postCaseModule(qps: CaseModulePostQPS, caseId: number, caseModuleId: number): Observable<CaseModulePostResponse> {
        this.modulesState.setLoadingCreatingCaseModule(true);
        const promise: Promise<CaseModulePostResponse> = new Promise((res, rej) => {
            this.modulesService.postCaseModule(qps, caseId, caseModuleId).pipe(
                tap((newCaseModule: CaseModulePostResponse) => {
                    const cm = newCaseModule?.data?.caseModule;
                    if (cm) {
                        this.modulesState.upsertCaseModule(cm);
                    }

                    const provisions = newCaseModule?.data?.provisions ?? [];
                    const supplies = newCaseModule?.data?.supplies ?? [];
                    const merged = [...provisions, ...supplies];
                    if (merged.length) {
                        this.provisionsFacade.upsertProvisions(merged as any);
                    }
                }),
                finalize(() => this.modulesState.setLoadingCreatingCaseModule(false))
            ).subscribe({
                next: (response: CaseModulePostResponse) => {
                    res(response);
                },
                error: e => { rej(e) }
            })
        });
        return from(promise);
    }

    patchCaseModule(qps: CaseModulePatchQPS, caseId: number, moduleId: number): Observable<CaseModulePostResponse> {
        this.modulesState.setLoadingUpdatingCaseModule(true);
        const promise: Promise<CaseModulePostResponse> = new Promise((res, rej) => {
            this.modulesService.patchCaseModule(qps, caseId, moduleId).pipe(
                tap((newCaseModule: CaseModulePostResponse) => {
                    const cm = newCaseModule?.data?.caseModule;
                    if (cm) {
                        this.modulesState.upsertCaseModule(cm);
                    }

                    const provisions = newCaseModule?.data?.provisions ?? [];
                    const supplies = newCaseModule?.data?.supplies ?? [];
                    const merged = [...provisions, ...supplies];
                    if (merged.length) {
                        this.provisionsFacade.upsertProvisions(merged as any);
                    }
                }),
                finalize(() => this.modulesState.setLoadingUpdatingCaseModule(false))
            ).subscribe({
                next: (response: CaseModulePostResponse) => {
                    res(response);
                },
                error: e => { rej(e) }
            })
        });
        return from(promise);
    }

    deleteCaseModule(caseId: number, caseModuleId: number, force: boolean = false): Observable<DeleteCaseModuleResult> {
        const promise: Promise<DeleteCaseModuleResult> = new Promise((res, rej) => {
            this.modulesService.deleteCaseModule(caseId, caseModuleId, force).pipe(
                tap(() => {
                    this.modulesState.deleteCaseModule(caseModuleId);
                    this.modulesState.deleteModuleAttentionsByCaseModuleId(caseModuleId);
                    this.attentionsState.deleteAttentionsByCaseModuleId(caseModuleId);
                })
            ).subscribe({
                next: () => {
                    this.provisionsFacade.loadAllProvisionsForCase(caseId).subscribe({
                        next: () => res({}),
                        error: refreshError => res({ refreshError }),
                    });
                },
                error: e => { rej(e) }
            })
        });
        return from(promise);
    }

    getModulesAttentions$(): Observable<Attention[]> {
        return this.modulesState.getModuleAttentions$();
    }

    getModulesAttentions(): Attention[] {
        return this.modulesState.getModuleAttentions();
    }

    isLoadingUpdatingCaseModule$(): Observable<boolean> {
        return this.modulesState.isLoadingUpdatingCaseModule$();
    }

    isLoadingCreatingCaseModule$(): Observable<boolean> {
        return this.modulesState.isLoadingCreatingCaseModule$();
    }

    cleanCaseModulesState() {
        this.modulesState.setCaseModules([]);
    }

}
